import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import '@/App.css';
import { Background } from './components/game/Background';
import SplashScreen from './screens/SplashScreen';
import TableSelectScreen from './screens/TableSelectScreen';
import LearnScreen from './screens/LearnScreen';
import QuizScreen from './screens/QuizScreen';
import ResultScreen from './screens/ResultScreen';
import ProgressScreen from './screens/ProgressScreen';
import RecordVoiceScreen from './screens/RecordVoiceScreen';
import { buildTable, shuffle } from './lib/tabuada';
import {
  getProgress,
  saveProgress,
  patchProgress,
  markLearned,
  recordSession,
  resetProgress,
  hydrate,
  setRemotePush,
} from './lib/storage';
import { stopSpeaking } from './lib/speech';
import { setMuted, unlockAudio } from './lib/sound';
import { api } from './lib/api';

const pageVariants = {
  initial: { opacity: 0, scale: 0.98, y: 12 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.98, y: -12 },
};

export default function App({ user, onLogout }) {
  const [progress, setProgress] = useState(getProgress());
  const [screen, setScreen] = useState(progress.name ? 'home' : 'splash');
  const [name, setName] = useState(progress.name || '');
  const [voiceId, setVoiceId] = useState(progress.voiceId || 'girl_2');
  const [volume, setVolume] = useState(progress.volume != null ? progress.volume : 1);
  const [table, setTable] = useState(null);
  const [mode, setMode] = useState('sequential');
  const [questions, setQuestions] = useState([]);
  const [lastStats, setLastStats] = useState(null);
  const [reviewQ, setReviewQ] = useState([]);
  const [resultVariant, setResultVariant] = useState('sequential');

  useEffect(() => {
    setMuted(volume === 0);
  }, [volume]);

  // Load progress saved on the account, then keep the server in sync.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.getProgress();
        if (res.data) {
          hydrate(res.data);
          if (mounted) {
            setProgress(res.data);
            if (res.data.name) {
              setName(res.data.name);
              setVoiceId(res.data.voiceId || 'girl_2');
              setVolume(res.data.volume != null ? res.data.volume : 1);
              setScreen('home');
            }
          }
        } else {
          api.putProgress(getProgress()).catch(() => {});
        }
      } catch {
        /* offline / not synced — local storage still works */
      }
      setRemotePush((d) => {
        api.putProgress(d).catch(() => {});
      });
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const go = (s) => {
    stopSpeaking();
    window.scrollTo(0, 0);
    setScreen(s);
  };

  const handleStart = (childName) => {
    setName(childName);
    setProgress(patchProgress({ name: childName, voiceId, volume }));
    go('home');
  };

  const handleSelectVoice = (id) => {
    setVoiceId(id);
    setProgress(patchProgress({ voiceId: id }));
  };

  const handleStartLearn = (t) => {
    unlockAudio();
    setTable(t);
    setReviewQ([]);
    go('learn');
  };

  const handleRecordVoice = (t) => {
    setTable(t);
    go('record');
  };

  const handleGoAnswer = (t) => {
    setProgress(markLearned(t));
    setMode('sequential');
    setQuestions(buildTable(t));
    go('quiz');
  };

  const mergeReview = (wrongQuestions) => {
    setReviewQ((prev) => {
      const map = new Map(prev.map((q) => [`${q.a}x${q.b}`, q]));
      (wrongQuestions || []).forEach((q) => map.set(`${q.a}x${q.b}`, q));
      return [...map.values()];
    });
  };

  const handleQuizComplete = (stats) => {
    setLastStats(stats);
    setProgress(recordSession(table, stats));
    mergeReview(stats.wrongQuestions);
    if (mode === 'sequential') setResultVariant('sequential');
    else if (mode === 'random') setResultVariant('random');
    else setResultVariant('review');
    go('result');
  };

  const startRandom = () => {
    setMode('random');
    setQuestions(shuffle(buildTable(table)));
    go('quiz');
  };

  const startReview = () => {
    setMode('review');
    setQuestions(shuffle(reviewQ));
    go('quiz');
  };

  const handleReset = () => {
    if (window.confirm('Recomeçar todo o progresso?')) {
      const fresh = resetProgress();
      setProgress(fresh);
      setName('');
      go('splash');
    }
  };

  const resultConfig = () => {
    const s = lastStats || {};
    if (resultVariant === 'sequential') {
      return {
        title: '🎉 Você terminou!',
        subtitle: `Agora vamos testar sua memória, ${name}!`,
        actions: [
          { label: 'Responder aleatoriamente', icon: 'dice', color: '#F97316', border: '#EA580C', onClick: startRandom, testid: 'start-random-btn' },
          { label: 'Ouvir de novo', icon: 'replay', color: '#38BDF8', border: '#0284C7', onClick: () => go('learn'), testid: 'replay-learn-btn' },
          { label: 'Escolher outra tabuada', icon: 'home', color: '#8B5CF6', border: '#7C3AED', onClick: () => go('home'), testid: 'home-btn' },
        ],
      };
    }
    if (resultVariant === 'random') {
      const acts = [];
      if (reviewQ.length > 0)
        acts.push({ label: 'Treinar o que errei', icon: 'review', color: '#EC4899', border: '#DB2777', onClick: startReview, testid: 'start-review-btn' });
      acts.push({ label: 'Meu progresso', icon: 'next', color: '#10B981', border: '#059669', onClick: () => go('progress'), testid: 'go-progress-btn' });
      acts.push({ label: 'Escolher outra tabuada', icon: 'home', color: '#8B5CF6', border: '#7C3AED', onClick: () => go('home'), testid: 'home-btn' });
      return {
        title: reviewQ.length ? '🏆 Muito bem!' : '🏆 Perfeito!',
        subtitle: `Você conseguiu ${s.correct ?? 0} de ${s.total ?? 0}!`,
        actions: acts,
      };
    }
    return {
      title: '💪 Você treinou!',
      subtitle: `Cada vez melhor, ${name}!`,
      actions: [
        { label: 'Meu progresso', icon: 'next', color: '#10B981', border: '#059669', onClick: () => go('progress'), testid: 'go-progress-btn' },
        { label: 'Escolher outra tabuada', icon: 'home', color: '#8B5CF6', border: '#7C3AED', onClick: () => go('home'), testid: 'home-btn' },
      ],
    };
  };

  const rc = resultConfig();

  return (
    <Background>
      <AnimatePresence mode="wait">
        <motion.div
          key={screen + (screen === 'quiz' ? mode : '')}
          variants={pageVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.28 }}
        >
          {screen === 'splash' && (
            <SplashScreen onStart={handleStart} voiceId={voiceId} volume={volume} />
          )}

          {screen === 'home' && (
            <TableSelectScreen
              name={name}
              progress={progress}
              selectedTable={table}
              selectedVoice={voiceId}
              volume={volume}
              onSelectTable={setTable}
              onSelectVoice={handleSelectVoice}
              onStartLearn={handleStartLearn}
              onOpenProgress={() => go('progress')}
              onLogout={onLogout}
              onRecordVoice={handleRecordVoice}
            />
          )}

          {screen === 'learn' && (
            <LearnScreen
              table={table}
              name={name}
              voiceId={voiceId}
              volume={volume}
              onVoiceChange={handleSelectVoice}
              onVolumeChange={(v) => {
                setVolume(v);
                setProgress(patchProgress({ volume: v }));
              }}
              onAnswer={handleGoAnswer}
              onBack={() => go('home')}
            />
          )}

          {screen === 'quiz' && (
            <QuizScreen
              mode={mode}
              table={table}
              name={name}
              voiceId={voiceId}
              volume={volume}
              questions={questions}
              onComplete={handleQuizComplete}
              onExit={() => go('home')}
            />
          )}

          {screen === 'result' && (
            <ResultScreen
              name={name}
              stats={lastStats}
              title={rc.title}
              subtitle={rc.subtitle}
              actions={rc.actions}
              voiceId={voiceId}
              volume={volume}
            />
          )}

          {screen === 'progress' && (
            <ProgressScreen
              progress={progress}
              onBack={() => go('home')}
              onReset={handleReset}
            />
          )}

          {screen === 'record' && (
            <RecordVoiceScreen
              table={table}
              name={name}
              onDone={() => go('home')}
              onBack={() => go('home')}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </Background>
  );
}
